import React from "react";
import Background from "../Background";
import ContainerEffectOverlay from "../ContainerEffectOverlay";
import {
  SlotRenderer,
  BackgroundMediaRenderer,
  EmptyMediaSlotAffordance,
  useHeroAnimation,
  useHeroAmbient,
} from "./heroModeUtils";
import "./heroModeAnimations.css";

/**
 * HeroMagazine — renderer for the static members of the Magazine
 * family: ``magazine`` (title on the left, supporting media on the
 * right) and its spatial mirror ``magazine_right`` (title on the
 * right, supporting media on the left). Same renderer handles both
 * because the only differences are the row direction and which side
 * the title's bottom line overlaps into — pure compositional flips,
 * not slot-contract changes.
 *
 * Editorial layout: the title is the visual anchor on one side; a
 * supporting media block sits on the other. The title's bottom line
 * visually overlaps the media via a small negative margin (toward
 * the media side) so the composition reads as a glossy magazine
 * cover instead of two adjacent panes.
 *
 * Falls back to a clean centered-title-over-background layout when the
 * media slot is empty, so the mode degrades gracefully when the user
 * picks Magazine before adding an image. Mobile collapses to a stacked
 * top-image / bottom-text composition (image first because the
 * editorial style reads "image then headline" at narrow widths) —
 * ``magazine_right`` keeps the same vertical order on mobile via
 * ``flex-col-reverse`` so the editorial reading rhythm doesn't flip
 * with the viewport.
 */

// Mirror of the Split renderer's ``isMediaRightAnchored`` helper,
// only here the *title* is the thing that flips sides. With the
// canonical ``magazine`` mode the title sits on the LEFT (so the
// media is on the right); ``magazine_right`` puts the title on the
// RIGHT (media on the left). We branch once on mode id and feed the
// result into both the desktop row direction (``flex-row`` →
// ``flex-row-reverse``) and the mobile column order
// (``flex-col`` → ``flex-col-reverse`` so the media keeps stacking
// above the text — the editorial "image then headline" reading
// order shouldn't flip just because the title moved sides on
// desktop).
function isTitleRightAnchored(modeId) {
  return modeId === "magazine_right";
}

const BP_CONFIG = {
  // ``mediaPct`` is how much of the row width the media column takes.
  // ``titleOverlap`` is the negative margin we apply to the title so
  // its bottom line drifts onto the media — the signature magazine
  // gesture. ``contentPx`` is the horizontal padding inside each
  // column.
  extra_large: { horizontal: true, mediaPct: "42%", contentPx: 64, titleOverlap: 32 },
  large:       { horizontal: true, mediaPct: "44%", contentPx: 56, titleOverlap: 28 },
  medium:      { horizontal: true, mediaPct: "46%", contentPx: 40, titleOverlap: 20 },
  small:       { horizontal: false, mediaMaxH: "38vh", contentPx: 24, contentPy: 28, titleOverlap: 0 },
  extra_small: { horizontal: false, mediaMaxH: "32vh", contentPx: 16, contentPy: 24, titleOverlap: 0 },
};

const HeroMagazine = ({ item, heroBase, heroSlots }) => {
  const {
    heroRootRef, containerClasses, containerStyle, fullBleedStyle,
    contentInsetStyle, responsiveHeight, baseHeightPx, bp,
  } = heroBase;

  const isAnimated = heroSlots?.isAnimated || false;
  const mediaSlotData = heroSlots?.getSlot("media");
  const hasMedia = !!mediaSlotData;

  const config = BP_CONFIG[bp] || BP_CONFIG.medium;
  const isHorizontal = config.horizontal && hasMedia;
  const titleOnRight = isTitleRightAnchored(item?.hero_mode);

  const entrance = heroSlots?.animatedEntrance || "fade-up";
  // Ken-Burns on the media half (mode-owned via
  // ``magazine_animated``); the editorial title stays still so the
  // motion doesn't compete with the typography.
  const mediaAmbientClass = useHeroAmbient(heroSlots?.ambientAnimation);

  const headingAnim = useHeroAnimation(isAnimated, entrance, { delay: 0 });
  const bodyAnim = useHeroAnimation(isAnimated, entrance, { delay: 200 });
  const ctaAnim = useHeroAnimation(isAnimated, entrance, { delay: 400 });

  // No media → centered title over the user's background. Same
  // graceful-degradation pattern as Split/Gallery.
  if (!hasMedia) {
    return (
      <div
        ref={heroRootRef}
        data-hero-root="true"
        className={containerClasses}
        style={{ ...containerStyle, ...fullBleedStyle }}
      >
        <Background
          bgImage={heroBase.bgImage}
          imageStyleClasses={heroBase.imageStyleClasses}
          imageInlineStyles={heroBase.imageInlineStyles}
          colorClass={heroBase.colorClass}
          colorStyle={heroBase.colorStyle}
          jsPatternLayer={heroBase.jsPatternLayer}
        />
        <ContainerEffectOverlay data={heroBase.data} />
        <div
          className={
            responsiveHeight
              ? "relative z-10 flex items-center justify-center w-full"
              : "absolute inset-0 z-10 flex items-center justify-center"
          }
          style={responsiveHeight ? { minHeight: `${baseHeightPx}px` } : undefined}
        >
          <div
            className="flex flex-col gap-4 w-full max-w-3xl mx-auto"
            style={{ padding: config.contentPx, ...contentInsetStyle }}
          >
            {/* Edit-mode "+ Add Image" affordance for the empty media
                slot. See HeroProfile / HeroSplit for the matching
                pattern. Hidden outside the editor. */}
            <EmptyMediaSlotAffordance
              heroSlots={heroSlots}
              heroId={item?.id}
              slotName="media"
              order={0}
            />
            <SlotRenderer slotName="heading" heroSlots={heroSlots} heroBase={heroBase} heroId={item?.id} slotIndex={1} />
            <SlotRenderer slotName="body" heroSlots={heroSlots} heroBase={heroBase} heroId={item?.id} slotIndex={2} />
            <SlotRenderer slotName="cta" heroSlots={heroSlots} heroBase={heroBase} heroId={item?.id} slotIndex={3} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={heroRootRef}
      data-hero-root="true"
      className={containerClasses}
      style={{ ...containerStyle, ...fullBleedStyle }}
    >
      <Background
        bgImage={heroBase.bgImage}
        imageStyleClasses={heroBase.imageStyleClasses}
        imageInlineStyles={heroBase.imageInlineStyles}
        colorClass={heroBase.colorClass}
        colorStyle={heroBase.colorStyle}
        jsPatternLayer={heroBase.jsPatternLayer}
      />
      <ContainerEffectOverlay data={heroBase.data} />

      <div
        className={`${
          isHorizontal
            ? titleOnRight ? "flex flex-row-reverse" : "flex flex-row"
            : titleOnRight ? "flex flex-col-reverse" : "flex flex-col"
        } w-full ${
          responsiveHeight ? "relative z-10" : "absolute inset-0 z-10"
        }`}
        style={responsiveHeight ? { minHeight: `${baseHeightPx}px` } : undefined}
      >
        {/* Text column. Title is anchored toward its OUTER edge
            (left for ``magazine``, right for ``magazine_right``);
            ``item_field_overrides`` already aligns the heading /
            body text to that side. Here we apply the negative
            margin on the side that touches the media column so the
            title's bottom line drifts INTO the media — the
            magazine gesture. For ``magazine`` (text on the left,
            media on the right) that's the right side; for
            ``magazine_right`` (text on the right, media on the
            left) that's the left side. ``z-10`` keeps text above
            the media even when the title overflows. ``min-w-0`` is
            required so the column honours its ``flex-1``
            allocation rather than growing to its widest child's
            intrinsic width — see HeroSplit's matching comment. */}
        <div
          className="flex flex-col justify-center flex-1 min-w-0 relative z-10"
          style={{
            ...(isHorizontal
              ? {
                  paddingLeft: config.contentPx,
                  paddingRight: config.contentPx,
                  // Overlap into the media column on whichever side
                  // it sits. With ``flex-row-reverse`` the visual
                  // adjacency is title-on-right + media-on-left, so
                  // the negative left margin is what pulls the
                  // title's bottom line into the media half (the
                  // mirror of the canonical ``marginRight``).
                  ...(titleOnRight
                    ? { marginLeft: -config.titleOverlap }
                    : { marginRight: -config.titleOverlap }),
                }
              : {
                  paddingLeft: config.contentPx,
                  paddingRight: config.contentPx,
                  paddingTop: config.contentPy,
                  paddingBottom: config.contentPy,
                }),
          }}
        >
          <div
            ref={headingAnim.ref}
            className={headingAnim.className}
            style={{ ...headingAnim.style, marginBottom: config.titleOverlap > 0 ? 8 : 16 }}
          >
            <SlotRenderer slotName="heading" heroSlots={heroSlots} heroBase={heroBase} heroId={item?.id} slotIndex={1} />
          </div>
          <div ref={bodyAnim.ref} className={bodyAnim.className} style={{ ...bodyAnim.style, marginBottom: 16 }}>
            <SlotRenderer slotName="body" heroSlots={heroSlots} heroBase={heroBase} heroId={item?.id} slotIndex={2} />
          </div>
          <div ref={ctaAnim.ref} className={ctaAnim.className} style={ctaAnim.style}>
            <SlotRenderer slotName="cta" heroSlots={heroSlots} heroBase={heroBase} heroId={item?.id} slotIndex={3} />
          </div>
        </div>

        {/* Media column. Sits behind the text column at the overlap
            point on desktop (the text column has ``z-10``); on mobile
            the media stacks *above* the text via ``order: -1`` —
            the editorial reading order is "image first, then
            headline".

            Sizing rules differ between desktop and mobile; see the
            matching comment in HeroSplit for the full reasoning.
            Short version:
              - Desktop: no explicit height — let the parent row's
                ``minHeight`` and flexbox's ``align-items: stretch``
                default size the column.
              - Mobile: explicit ``minHeight`` (paired with
                ``maxHeight``) on the column itself, because in a
                stacked ``flex-col`` parent ``align-items: stretch``
                governs width, not height, and the only child
                (``BackgroundMediaRenderer``) is ``position:
                absolute`` and contributes nothing to flow. Without
                this, the column collapses to ``0`` and the image
                disappears on small screens. */}
        <div
          className="relative overflow-hidden flex-shrink-0"
          style={
            isHorizontal
              ? { width: config.mediaPct }
              : {
                  width: "100%",
                  minHeight: config.mediaMaxH,
                  maxHeight: config.mediaMaxH,
                  // Mobile stacking. We want media on TOP, text on
                  // bottom regardless of which side the title sits
                  // on at desktop widths — the editorial reading
                  // rhythm is "image first, then headline".
                  //   * Canonical (text-on-left, ``flex-col``): media
                  //     is the second child in source, so we use
                  //     ``order: -1`` to pull it above the text.
                  //   * Mirror (text-on-right, ``flex-col-reverse``):
                  //     ``column-reverse`` already inverts source
                  //     order so the second child (media) renders on
                  //     top — applying ``order: -1`` here would
                  //     interact with the reverse and put text back
                  //     on top. Leaving order at the default keeps
                  //     the visual stack aligned with the canonical
                  //     variant.
                  ...(titleOnRight ? null : { order: -1 }),
                }
          }
        >
          <BackgroundMediaRenderer
            slotData={mediaSlotData}
            heroBase={heroBase}
            ambientClass={mediaAmbientClass}
            heroItem={item}
          />
        </div>
      </div>
    </div>
  );
};

export default React.memo(HeroMagazine);
